import React, { useState, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  FileText,
  Filter,
  RefreshCw,
  Eye,
  Trash2,
  Plus,
  Search,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  Building2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import jsPDF from "jspdf";
import api from "../../utils/axios.config";
import useLogo from "../../hooks/useLogo";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const FinancialReports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState("monthly");
  const [chartType, setChartType] = useState("bar");
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const reportContentRef = useRef(null);
  const chartRef = useRef(null);
  const queryClient = useQueryClient();
  const { data: logoUrl } = useLogo();

  // Fetch financial reports
  const { data: reportsData, isLoading: reportsLoading } = useQuery({
    queryKey: ["financialReports"],
    queryFn: async () => await api.get("finance/reports"),
    staleTime: 5 * 60 * 1000,
  });

  const reports = reportsData?.data?.data?.reports || [];

  // Generate report mutation
  const generateReportMutation = useMutation({
    mutationFn: async (reportData) => {
      return api.post("finance/report/generate", reportData);
    },
    onSuccess: () => {
      toast.success("Financial report generated successfully");
      queryClient.invalidateQueries(["financialReports"]);
      setShowGenerateModal(false);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to generate report"
      );
    },
  });

  // Delete report mutation
  const deleteReportMutation = useMutation({
    mutationFn: async (reportId) => {
      return api.delete(`finance/reports/${reportId}`);
    },
    onSuccess: () => {
      toast.success("Report deleted successfully");
      queryClient.invalidateQueries(["financialReports"]);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to delete report");
    },
  });

  const handleGenerateReport = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const reportData = {
      reportType: selectedReportType,
      startDate: formData.get("startDate"),
      endDate: formData.get("endDate"),
      title:
        formData.get("title") ||
        `${selectedReportType} Report - ${new Date().toLocaleDateString()}`,
    };
    generateReportMutation.mutate(reportData);
  };

  // Chart configurations
  const getChartData = (report) => {
    if (!report || !report.incomeBreakdown) return null;

    const incomeData = report.incomeBreakdown || [];
    const expenseData = report.expenseBreakdown || [];

    const categories = [
      ...new Set([
        ...incomeData.map((item) => item.category),
        ...expenseData.map((item) => item.category),
      ]),
    ];

    return {
      bar: {
        labels: categories,
        datasets: [
          {
            label: "আয় (৳)",
            data: categories.map((cat) => {
              const income = incomeData.find((item) => item.category === cat);
              return income ? income.amount : 0;
            }),
            backgroundColor: "rgba(34, 197, 94, 0.8)",
            borderColor: "rgb(34, 197, 94)",
            borderWidth: 1,
          },
          {
            label: "খরচ (৳)",
            data: categories.map((cat) => {
              const expense = expenseData.find((item) => item.category === cat);
              return expense ? expense.amount : 0;
            }),
            backgroundColor: "rgba(239, 68, 68, 0.8)",
            borderColor: "rgb(239, 68, 68)",
            borderWidth: 1,
          },
        ],
      },
      pie: {
        labels: incomeData.map((item) => item.category),
        datasets: [
          {
            label: "আয় বিতরণ",
            data: incomeData.map((item) => item.amount),
            backgroundColor: [
              "#10B981",
              "#3B82F6",
              "#8B5CF6",
              "#F59E0B",
              "#EF4444",
              "#6B7280",
              "#EC4899",
              "#14B8A6",
            ],
            borderWidth: 2,
          },
        ],
      },
      line: report.monthlyTrends
        ? {
            labels: report.monthlyTrends.map(
              (trend) => `${trend.month}/${trend.year}`
            ),
            datasets: [
              {
                label: "আয়",
                data: report.monthlyTrends.map((trend) => trend.income),
                borderColor: "rgb(34, 197, 94)",
                backgroundColor: "rgba(34, 197, 94, 0.1)",
                tension: 0.4,
              },
              {
                label: "খরচ",
                data: report.monthlyTrends.map((trend) => trend.expense),
                borderColor: "rgb(239, 68, 68)",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                tension: 0.4,
              },
              {
                label: "পেমেন্ট",
                data: report.monthlyTrends.map((trend) => trend.payments),
                borderColor: "rgb(59, 130, 246)",
                backgroundColor: "rgba(59, 130, 246, 0.1)",
                tension: 0.4,
              },
            ],
          }
        : null,
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${
              context.dataset.label
            }: ৳${context.parsed.y?.toLocaleString()}`;
          },
        },
      },
    },
    scales:
      chartType !== "pie"
        ? {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function (value) {
                  return "৳" + value.toLocaleString();
                },
              },
            },
          }
        : {},
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("bn-BD", {
      style: "currency",
      currency: "BDT",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Canvas-based PDF generation with proper Bangla font support
  const generateCanvasReport = async () => {
    if (!selectedReport) {
      toast.error('No report selected for export');
      return;
    }

    setIsExportingPDF(true);

    try {
      // Load SolaimanLipi font like in AllUser.js
      await document.fonts.load("48px SolaimanLipi");
      await document.fonts.load("36px SolaimanLipi");
      await document.fonts.load("28px SolaimanLipi");
      await document.fonts.load("24px SolaimanLipi");
      await document.fonts.load("20px SolaimanLipi");
      await document.fonts.load("18px SolaimanLipi");

      // Create canvas for the report
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      // A4 size in pixels (150 DPI for smaller file size)
      canvas.width = 1240; // 8.27 inches * 150 DPI
      canvas.height = 1754; // 11.69 inches * 150 DPI

      // White background
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      let yPos = 80;
      
      // Load and draw logo
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.src = logoUrl || "/logo.png";

      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = () => {
          console.warn("Could not load logo");
          resolve(); // Continue without logo
        };
      });

      // Draw logo
      if (logoImg.complete && logoImg.naturalHeight !== 0) {
        ctx.drawImage(logoImg, 80, 30, 100, 100);
      }

      // Organization header
      ctx.fillStyle = "black";
      ctx.font = "bold 28px SolaimanLipi";
      ctx.textAlign = "center";
      ctx.fillText("নিঃস্বার্থ নবজীবন সংগঠন- NNS", canvas.width / 2, yPos);
      
      ctx.font = "18px SolaimanLipi";
      ctx.fillText("স্বার্থপর মানুষের ভিড়ে নিঃস্বার্থভাবে আমরাই অবিচল", canvas.width / 2, yPos + 35);

      yPos += 120;

      // Report title
      ctx.font = "bold 26px SolaimanLipi";
      ctx.fillText("আর্থিক প্রতিবেদন", canvas.width / 2, yPos);
      
      yPos += 50;

      // Report details
      ctx.font = "20px SolaimanLipi";
      
      // Use the original title as provided by user
      ctx.fillText(selectedReport.title, canvas.width / 2, yPos);
      
      yPos += 40;

      // Date in Bangla
      const reportDate = new Date(selectedReport.generatedAt);
      const banglaMonths = [
        'জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন',
        'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'
      ];
      const formattedDate = `${reportDate.getDate()} ${banglaMonths[reportDate.getMonth()]}, ${reportDate.getFullYear()}`;
      ctx.font = "16px SolaimanLipi";
      ctx.fillText(`প্রতিবেদনের তারিখ: ${formattedDate}`, canvas.width / 2, yPos);

      yPos += 80;

      // Add a decorative line
      ctx.strokeStyle = "#006400";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(100, yPos - 20);
      ctx.lineTo(canvas.width - 100, yPos - 20);
      ctx.stroke();

      yPos += 20;

      // Financial summary section
      if (selectedReport.summary) {
        ctx.textAlign = "left";
        ctx.font = "bold 22px SolaimanLipi";
        ctx.fillText("আর্থিক সারসংক্ষেপ", 80, yPos);
        
        yPos += 50;

        ctx.font = "18px SolaimanLipi";
        const summaryData = [
          ['মোট আয়', `৳ ${selectedReport.summary.totalIncome?.toLocaleString() || 0}`],
          ['মোট খরচ', `৳ ${selectedReport.summary.totalExpense?.toLocaleString() || 0}`],
          ['মোট পেমেন্ট', `৳ ${selectedReport.summary.totalPayments?.toLocaleString() || 0}`],
          ['নিট ব্যালেন্স', `৳ ${selectedReport.summary.netBalance?.toLocaleString() || 0}`],
          ['মোট লেনদেন', selectedReport.summary.transactionCount?.toString() || '0']
        ];

        summaryData.forEach(([label, value]) => {
          // Add background for alternating rows
          if (summaryData.indexOf([label, value]) % 2 === 0) {
            ctx.fillStyle = "#f8f9fa";
            ctx.fillRect(70, yPos - 20, canvas.width - 140, 30);
          }
          
          ctx.fillStyle = "black";
          ctx.fillText(`${label}: ${value}`, 80, yPos);
          yPos += 35;
        });

        yPos += 40;
      }

      // Income breakdown
      if (selectedReport.incomeBreakdown && selectedReport.incomeBreakdown.length > 0) {
        ctx.font = "bold 20px SolaimanLipi";
        ctx.fillText("আয়ের বিভাগ", 80, yPos);
        yPos += 45;

        ctx.font = "16px SolaimanLipi";
        selectedReport.incomeBreakdown.forEach((item, index) => {
          // Add background for alternating rows
          if (index % 2 === 0) {
            ctx.fillStyle = "#e8f5e8";
            ctx.fillRect(70, yPos - 18, canvas.width - 140, 28);
          }
          
          ctx.fillStyle = "#006400";
          ctx.fillText(`• ${item.category}: ৳${item.amount.toLocaleString()} (${item.percentage.toFixed(1)}%)`, 80, yPos);
          yPos += 30;
        });

        yPos += 40;
      }

      // Expense breakdown
      if (selectedReport.expenseBreakdown && selectedReport.expenseBreakdown.length > 0) {
        ctx.font = "bold 20px SolaimanLipi";
        ctx.fillStyle = "black";
        ctx.fillText("খরচের বিভাগ", 80, yPos);
        yPos += 45;

        ctx.font = "16px SolaimanLipi";
        selectedReport.expenseBreakdown.forEach((item, index) => {
          // Add background for alternating rows
          if (index % 2 === 0) {
            ctx.fillStyle = "#ffeaea";
            ctx.fillRect(70, yPos - 18, canvas.width - 140, 28);
          }
          
          ctx.fillStyle = "#dc2626";
          ctx.fillText(`• ${item.category}: ৳${item.amount.toLocaleString()} (${item.percentage.toFixed(1)}%)`, 80, yPos);
          yPos += 30;
        });

        yPos += 40;
      }

      // Add chart if available
      if (chartRef.current) {
        try {
          // Import html2canvas dynamically to capture the chart
          const html2canvas = (await import('html2canvas')).default;
          
          const chartCanvas = await html2canvas(chartRef.current, {
            backgroundColor: "#ffffff",
            scale: 1,
            logging: false,
            useCORS: true,
          });

          // Calculate chart dimensions to fit in our canvas
          const chartWidth = canvas.width - 160; // Use most of the canvas width
          const chartHeight = Math.min(
            (chartCanvas.height * chartWidth) / chartCanvas.width,
            canvas.height - yPos - 150 // Leave space for footer
          );
          
          ctx.font = "bold 20px SolaimanLipi";
          ctx.fillStyle = "black";
          ctx.fillText("চার্ট বিশ্লেষণ", 80, yPos);
          yPos += 45;

          // Add chart border
          ctx.strokeStyle = "#ddd";
          ctx.lineWidth = 2;
          ctx.strokeRect(75, yPos - 5, chartWidth + 10, chartHeight + 10);
          
          // Draw the chart image on canvas
          ctx.drawImage(chartCanvas, 80, yPos, chartWidth, chartHeight);
          yPos += chartHeight + 50;
        } catch (error) {
          console.warn("Could not add chart to canvas:", error);
        }
      }

      // Add final decorative elements and footer
      const footerY = canvas.height - 80;
      
      // Add decorative line before footer
      ctx.strokeStyle = "#006400";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(100, footerY - 20);
      ctx.lineTo(canvas.width - 100, footerY - 20);
      ctx.stroke();

      // Footer
      ctx.textAlign = "center";
      ctx.font = "14px SolaimanLipi";
      ctx.fillStyle = "#006400";
      ctx.fillText("নিঃস্বার্থ নবজীবন সংগঠন আর্থিক সিস্টেম", canvas.width / 2, footerY);
      
      // Add date generated
      ctx.font = "12px SolaimanLipi";
      ctx.fillStyle = "#666";
      ctx.fillText(`প্রতিবেদন তৈরির সময়: ${new Date().toLocaleDateString('bn-BD')}`, canvas.width / 2, footerY + 25);

      // Convert canvas to PDF with compression
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/jpeg', 0.8); // Use JPEG with 80% quality for smaller size
      
      // Calculate dimensions to fit A4
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

      // Save PDF
      const filename = `${selectedReport.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

      toast.success('PDF exported successfully!');

    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  // Use the Canvas-based PDF generation as the main export function
  const exportToPDF = generateCanvasReport;

  if (reportsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              আর্থিক রিপোর্ট
            </h1>
            <p className="text-gray-600">
              সংগঠনের আর্থিক লেনদেনের বিস্তারিত রিপোর্ট এবং বিশ্লেষণ
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowGenerateModal(true)}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors duration-200 flex items-center"
            >
              <Plus size={16} className="mr-2" />
              নতুন রিপোর্ট
            </button>

            <button
              onClick={() =>
                queryClient.invalidateQueries(["financialReports"])
              }
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center"
            >
              <RefreshCw size={16} className="mr-2" />
              রিফ্রেশ
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Reports List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                তৈরিকৃত রিপোর্ট
              </h2>
              <p className="text-sm text-gray-600">
                {reports.length} টি রিপোর্ট উপলব্ধ
              </p>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {reports.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">কোন রিপোর্ট পাওয়া যায়নি</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {reports.map((report) => (
                    <div
                      key={report._id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-150 ${
                        selectedReport?._id === report._id
                          ? "bg-primary/5 border-r-2 border-primary"
                          : ""
                      }`}
                      onClick={() => setSelectedReport(report)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {report.title}
                        </h3>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            report.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : report.status === "generating"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {report.status === "completed"
                            ? "সম্পূর্ণ"
                            : report.status === "generating"
                            ? "তৈরি হচ্ছে"
                            : "ব্যর্থ"}
                        </span>
                      </div>

                      <div className="text-xs text-gray-500 mb-2">
                        <span className="capitalize">{report.reportType}</span>{" "}
                        •
                        {new Date(report.generatedAt).toLocaleDateString(
                          "bn-BD"
                        )}
                      </div>

                      {report.summary && (
                        <div className="text-xs text-gray-600">
                          নেট: {formatAmount(report.summary.netBalance)}
                        </div>
                      )}

                      <div className="flex items-center justify-end mt-3 space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReport(report);
                          }}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors duration-150"
                          title="বিস্তারিত দেখুন"
                        >
                          <Eye size={14} />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteReportMutation.mutate(report._id);
                          }}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors duration-150"
                          title="ডিলিট করুন"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Report Details & Charts */}
        <div className="lg:col-span-2">
          {selectedReport ? (
            <div className="space-y-6">
              {/* Report Summary */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedReport.title}
                  </h2>
                  <div className="flex items-center gap-2">
                    <select
                      value={chartType}
                      onChange={(e) => setChartType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="bar">বার চার্ট</option>
                      <option value="pie">পাই চার্ট</option>
                      {selectedReport.monthlyTrends && (
                        <option value="line">লাইন চার্ট</option>
                      )}
                    </select>

                    <button
                      onClick={exportToPDF}
                      disabled={isExportingPDF}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                    >
                      {isExportingPDF ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-1"></div>
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download size={14} className="mr-1" />
                          PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {selectedReport.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            মোট আয়
                          </p>
                          <p className="text-lg font-bold text-green-900">
                            {formatAmount(selectedReport.summary.totalIncome)}
                          </p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-green-600" />
                      </div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-red-800">
                            মোট খরচ
                          </p>
                          <p className="text-lg font-bold text-red-900">
                            {formatAmount(selectedReport.summary.totalExpense)}
                          </p>
                        </div>
                        <TrendingDown className="w-8 h-8 text-red-600" />
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800">
                            পেমেন্ট
                          </p>
                          <p className="text-lg font-bold text-blue-900">
                            {formatAmount(selectedReport.summary.totalPayments)}
                          </p>
                        </div>
                        <DollarSign className="w-8 h-8 text-blue-600" />
                      </div>
                    </div>

                    <div
                      className={`${
                        selectedReport.summary.netBalance >= 0
                          ? "bg-green-50 border-green-200"
                          : "bg-red-50 border-red-200"
                      } border rounded-lg p-4`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p
                            className={`text-sm font-medium ${
                              selectedReport.summary.netBalance >= 0
                                ? "text-green-800"
                                : "text-red-800"
                            }`}
                          >
                            নেট ব্যালেন্স
                          </p>
                          <p
                            className={`text-lg font-bold ${
                              selectedReport.summary.netBalance >= 0
                                ? "text-green-900"
                                : "text-red-900"
                            }`}
                          >
                            {formatAmount(selectedReport.summary.netBalance)}
                          </p>
                        </div>
                        <Building2
                          className={`w-8 h-8 ${
                            selectedReport.summary.netBalance >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Charts */}
              {selectedReport.status === "completed" && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {chartType === "bar"
                      ? "বার চার্ট বিশ্লেষণ"
                      : chartType === "pie"
                      ? "পাই চার্ট বিশ্লেষণ"
                      : "মাসিক ট্রেন্ড বিশ্লেষণ"}
                  </h3>

                  <div className="h-96" ref={chartRef}>
                    {(() => {
                      const chartData = getChartData(selectedReport);
                      if (!chartData || !chartData[chartType]) {
                        return (
                          <div className="flex items-center justify-center h-full">
                            <p className="text-gray-500">
                              চার্ট ডেটা উপলব্ধ নেই
                            </p>
                          </div>
                        );
                      }

                      switch (chartType) {
                        case "bar":
                          return (
                            <Bar data={chartData.bar} options={chartOptions} />
                          );
                        case "pie":
                          return (
                            <Pie data={chartData.pie} options={chartOptions} />
                          );
                        case "line":
                          return (
                            <Line
                              data={chartData.line}
                              options={chartOptions}
                            />
                          );
                        default:
                          return null;
                      }
                    })()}
                  </div>
                </div>
              )}

              {/* Breakdown Tables */}
              {selectedReport.status === "completed" &&
                selectedReport.incomeBreakdown && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Income Breakdown */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                        আয়ের বিভাগ
                      </h3>

                      <div className="space-y-3">
                        {selectedReport.incomeBreakdown.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.category}
                              </p>
                              <p className="text-xs text-gray-600">
                                {item.count} টি লেনদেন
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-green-700">
                                {formatAmount(item.amount)}
                              </p>
                              <p className="text-xs text-gray-600">
                                {item.percentage.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Expense Breakdown */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <TrendingDown className="w-5 h-5 mr-2 text-red-600" />
                        খরচের বিভাগ
                      </h3>

                      <div className="space-y-3">
                        {selectedReport.expenseBreakdown.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.category}
                              </p>
                              <p className="text-xs text-gray-600">
                                {item.count} টি লেনদেন
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-red-700">
                                {formatAmount(item.amount)}
                              </p>
                              <p className="text-xs text-gray-600">
                                {item.percentage.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <BarChart3 size={64} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                রিপোর্ট নির্বাচন করুন
              </h3>
              <p className="text-gray-600">
                বিস্তারিত বিশ্লেষণ দেখতে বামে থেকে একটি রিপোর্ট নির্বাচন করুন
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Generate Report Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                নতুন রিপোর্ট তৈরি করুন
              </h3>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleGenerateReport} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  রিপোর্টের ধরন
                </label>
                <select
                  value={selectedReportType}
                  onChange={(e) => setSelectedReportType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="monthly">মাসিক</option>
                  <option value="yearly">বার্ষিক</option>
                  <option value="quarterly">ত্রৈমাসিক</option>
                  <option value="custom">কাস্টম</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  রিপোর্টের নাম
                </label>
                <input
                  type="text"
                  name="title"
                  placeholder="রিপোর্টের নাম লিখুন"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    শুরুর তারিখ
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    defaultValue={dateRange.startDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    শেষের তারিখ
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    defaultValue={dateRange.endDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  disabled={generateReportMutation.isLoading}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50 transition-colors duration-200 flex items-center"
                >
                  {generateReportMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      তৈরি হচ্ছে...
                    </>
                  ) : (
                    <>
                      <FileText size={16} className="mr-2" />
                      রিপোর্ট তৈরি করুন
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReports;
